// React component (OnsitePayments.js)
import React, { useState, useEffect } from "react";
import {
  FaCalendar,
  FaUser,
  FaFileAlt,
  FaTimes,
  FaExclamationCircle,
  FaSyncAlt,
  FaCheck,
  FaMoneyBillWave,
} from "react-icons/fa";
import { FiEye, FiSearch } from "react-icons/fi";
import Select from "react-select";
import config from "../../config";
import Cookies from "js-cookie";
import styles from "../css/css_pages/OnsitePayments.module.css";

const OnsitePayments = () => {
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [approvalStatus, setApprovalStatus] = useState("pending");
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [isApproving, setIsApproving] = useState(false);
  const [tsos, setTsos] = useState([]);
  const [selectedTsoFilter, setSelectedTsoFilter] = useState(null);
  const [invoiceNoFilter, setInvoiceNoFilter] = useState("");
  const [filteredPayments, setFilteredPayments] = useState([]);

  const paymentTypeOptions = [
    { value: "cash", label: "Cash" },
    { value: "bank_of_ceylon", label: "Bank of Ceylon" },
    { value: "peoples_bank", label: "Peoples Bank" },
  ];

  useEffect(() => {
    fetchPaymentImages();
    fetchAllInvoices();
    fetchTsoList();
  }, []);

  const fetchAllInvoices = async () => {
    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/get-all-invoices`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setInvoices(data.response || []);
      } else {
        const errorData = await response.json();
        console.error("Error fetching invoices:", errorData.message);
      }
    } catch (err) {
      console.error("Error fetching invoices:", err);
    }
  };

  const fetchTsoList = async () => {
    try {
      const token = Cookies.get("authToken");
      if (!token) return;

      const response = await fetch(`${config.API_URL}/management/tso-list`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTsos(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Error fetching TSO list:", err);
    }
  };

  const getInvoiceDetails = (invoiceId) => {
    return invoices.find((invoice) => invoice.invoice_id === invoiceId);
  };

  // Filter payments based on TSO and Invoice No
  useEffect(() => {
    let result = [...payments];

    if (selectedTsoFilter && selectedTsoFilter.value !== "all") {
      result = result.filter((payment) => {
        // Handle both string and number comparisons
        const paymentTsoId = payment.tso_id?.toString();
        const selectedTsoId = selectedTsoFilter.value?.toString();
        return paymentTsoId === selectedTsoId;
      });
    }

    if (invoiceNoFilter) {
      result = result.filter((payment) => {
        // payment.invoice_id already contains the invoice number from backend
        const invoiceNo = payment.invoice_id || "";
        return String(invoiceNo)
          .toLowerCase()
          .includes(invoiceNoFilter.toLowerCase());
      });
    }

    // Sort by uploaded_date in descending order (newest first)
    result.sort((a, b) => {
      const dateA = a.uploaded_date ? new Date(a.uploaded_date) : new Date(0);
      const dateB = b.uploaded_date ? new Date(b.uploaded_date) : new Date(0);
      return dateB - dateA;
    });

    setFilteredPayments(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTsoFilter, invoiceNoFilter, payments, invoices]);

  const showMessageBox = (text, type = "success") => {
    setMessage({ text, type });
    setShowMessage(true);
    setTimeout(() => {
      setShowMessage(false);
    }, 3000);
  };

  const handleDownload = async () => {
    try {
      const token = Cookies.get("authToken");
      const response = await fetch(
        `${config.API_URL}/management/view-payment-image/${selectedPayment.payment_id}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to download image.");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = selectedPayment.stored_filename || "payment_image.jpg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err);
      showMessageBox("Failed to download the image.", "error");
    }
  };

  const fetchPaymentImages = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/get-all-payment-images`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPayments(data.response || []);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to fetch payment images");
      }
    } catch (err) {
      setError("Network error occurred while fetching payment images");
      console.error("Error fetching payment images:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (payment) => {
    setSelectedPayment(payment);
    setShowModal(true);
    setImageLoading(true);
    setImageUrl(null);
    setAmount(payment.payment_amount || "");
    setDescription(payment.description || "");
    setPaymentType(payment.payment_type || "");
    setApprovalStatus(payment.payment_status || "pending");

    const invoice = getInvoiceDetails(payment.invoice_id);
    setInvoiceDetails(invoice);

    const url = await fetchImageAsBlob(payment.payment_id);
    if (url) {
      setImageUrl(url);
    }
    setImageLoading(false);
  };

  const closeModal = () => {
    if (isApproving) return;
    setShowModal(false);
    setSelectedPayment(null);
    setInvoiceDetails(null);
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }
    setImageUrl(null);
    setImageLoading(false);
    setAmount("");
    setDescription("");
    setPaymentType("");
    setApprovalStatus("pending");
    setShowMessage(false);
  };

  const formatDateTime = (dateString, showOnlyTime = false) => {
    if (!dateString || isNaN(Date.parse(dateString))) {
      return "Invalid Date";
    }

    const date = new Date(dateString);

    const options = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC",
    };

    if (!showOnlyTime) {
      options.year = "numeric";
      options.month = "2-digit";
      options.day = "2-digit";
    }

    return date.toLocaleString("en-US", options).replace(",", "");
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "-";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "LKR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const fetchImageAsBlob = async (paymentId) => {
    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/view-payment-image/${paymentId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error("Error fetching image:", error);
      return null;
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === "") {
      setAmount("");
      return;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return;
    }

    if (numValue < 0) {
      showMessageBox("Amount cannot be negative", "error");
      return;
    }

    setAmount(value);
  };

  const handleApprovePayment = async () => {
    try {
      setIsApproving(true);

      if (!amount || !description || !paymentType) {
        showMessageBox("Please fill in all fields", "error");
        return;
      }

      const numAmount = parseFloat(amount);
      if (numAmount < 0) {
        showMessageBox("Amount cannot be negative", "error");
        return;
      }

      const token = Cookies.get("authToken");
      const response = await fetch(
        `${config.API_URL}/management/update-payment/${selectedPayment.payment_id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            amount: numAmount,
            invoice_no: selectedPayment.invoice_id,
            description: description,
            payment_type: paymentType,
            payment_status: "approved",
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to approve payment");
      }

      await response.json();

      setPayments((prevPayments) =>
        prevPayments.map((payment) =>
          payment.payment_id === selectedPayment.payment_id
            ? {
                ...payment,
                payment_status: "approved",
                payment_amount: numAmount,
                description: description,
                payment_type: paymentType,
              }
            : payment
        )
      );

      setApprovalStatus("approved");
      showMessageBox("Payment approved successfully!", "success");

      await fetchPaymentImages();
    } catch (error) {
      console.error("Error approving payment:", error);
      showMessageBox(
        error.message || "Failed to approve payment. Please try again.",
        "error"
      );
    } finally {
      setIsApproving(false);
    }
  };

  const handlePaymentTypeChange = (e) => {
    setPaymentType(e.target.value);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <FaSyncAlt className={styles.loadingSpinner} />
          <p className={styles.loadingText}>Loading payment images...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorContent}>
          <FaExclamationCircle className={styles.errorIcon} />
          <h3 className={styles.errorTitle}>Error Loading Data</h3>
          <p className={styles.errorMessage}>{error}</p>
          <button onClick={fetchPaymentImages} className={styles.retryButton}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {showMessage && (
        <div
          className={`${styles.messageBox} ${
            styles[`messageBox${message.type}`]
          }`}
        >
          <div className={styles.messageBoxContent}>
            {message.type === "error" ? (
              <FaExclamationCircle className={styles.messageBoxIcon} />
            ) : (
              <FaCheck className={styles.messageBoxIcon} />
            )}
            <span>{message.text}</span>
          </div>
        </div>
      )}

      <div className={styles.innerContainer}>
        <div className={styles.header}>
          <h1 className={styles.headerTitle}>Onsite Payment Images</h1>
          <p className={styles.headerSubtitle}>
            View and manage uploaded payment verification images
          </p>
        </div>

        <div className={styles.statsGrid}>
          <div className={styles.statsCard}>
            <div className={styles.statsCardContent}>
              <div className={styles.statsIcon}>
                <FaFileAlt className={`${styles.statsIcon} ${styles.blue}`} />
              </div>
              <div className={styles.statsDetails}>
                <p>Total Images</p>
                <p>{payments.length}</p>
              </div>
            </div>
          </div>

          <div className={styles.statsCard}>
            <div className={styles.statsCardContent}>
              <div className={styles.statsIcon}>
                <FaUser className={`${styles.statsIcon} ${styles.green}`} />
              </div>
              <div className={styles.statsDetails}>
                <p>Unique Clients</p>
                <p>{new Set(payments.map((p) => p.client_id)).size}</p>
              </div>
            </div>
          </div>

          <div className={styles.statsCard}>
            <div className={styles.statsCardContent}>
              <div className={styles.statsIcon}>
                <FaCalendar
                  className={`${styles.statsIcon} ${styles.purple}`}
                />
              </div>
              <div className={styles.statsDetails}>
                <p>Today's Uploads</p>
                <p>
                  {
                    payments.filter(
                      (p) =>
                        new Date(p.uploaded_date).toDateString() ===
                        new Date().toDateString()
                    ).length
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.filterSection}>
          <div className={styles.filterControls}>
            <div className={styles.filterItem}>
              <Select
                options={[
                  { value: "all", label: "All TSOs" },
                  ...tsos.map((tso) => ({
                    value: tso.tso_id,
                    label: tso.tso_calling_name || tso.tso_name,
                  })),
                ]}
                value={selectedTsoFilter}
                onChange={setSelectedTsoFilter}
                placeholder="Filter by TSO"
                isClearable
                isSearchable
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "42px",
                    borderColor: "#e2e8f0",
                    "&:hover": { borderColor: "#cbd5e1" },
                  }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </div>
            <div className={styles.filterItem}>
              <div className={styles.searchWrapper}>
                <FiSearch className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search by Invoice No..."
                  value={invoiceNoFilter}
                  onChange={(e) => setInvoiceNoFilter(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.tableContainer}>
          <div className={styles.tableHeader}>
            <h2 className={styles.tableTitle}>Payment Images</h2>
          </div>

          {filteredPayments.length === 0 ? (
            <div className={styles.emptyState}>
              <FaFileAlt className={styles.emptyIcon} />
              <h3 className={styles.emptyTitle}>No payment images found</h3>
              <p className={styles.emptySubtitle}>
                No payment verification images have been uploaded yet.
              </p>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className={styles.table}>
                <thead className={styles.tableHead}>
                  <tr>
                    <th className={styles.tableHeaderCell}>Payment ID</th>
                    <th className={styles.tableHeaderCell}>Client Name</th>
                    <th className={styles.tableHeaderCell}>TSO Name</th>
                    <th className={styles.tableHeaderCell}>Invoice No.</th>
                    <th className={styles.tableHeaderCell}>Status</th>
                    <th className={styles.tableHeaderCell}>Upload Date</th>
                    <th className={styles.tableHeaderCell}>Action</th>
                  </tr>
                </thead>
                <tbody className={styles.tableBody}>
                  {filteredPayments.map((payment) => (
                    <tr
                      key={payment.payment_id}
                      onClick={() => handleRowClick(payment)}
                      className={styles.tableRow}
                    >
                      <td className={`${styles.tableCell} ${styles.primary}`}>
                        {payment.payment_id.substring(0, 8)}...
                      </td>
                      <td className={`${styles.tableCell} ${styles.primary}`}>
                        {payment.client_id}
                      </td>
                      <td className={`${styles.tableCell} ${styles.primary}`}>
                        {payment.tso_name || "-"}
                      </td>
                      <td className={`${styles.tableCell} ${styles.primary}`}>
                        <div className={styles.invoiceCell}>
                          <div>{payment.invoice_id}</div>
                          <div className={styles.paymentAmount}>
                            {formatCurrency(payment.payment_amount)}
                          </div>
                        </div>
                      </td>
                      <td className={styles.tableCell}>
                        <span
                          className={`${styles.statusBadge} ${
                            payment.payment_status === "approved"
                              ? styles.statusApproved
                              : styles.statusPending
                          }`}
                        >
                          {payment.payment_status || "pending"}
                        </span>
                      </td>
                      <td className={`${styles.tableCell} ${styles.secondary}`}>
                        {formatDateTime(payment.uploaded_date)}
                      </td>
                      <td className={`${styles.tableCell} ${styles.secondary}`}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(payment);
                          }}
                          className={`${styles.actionButton} ${styles.preview}`}
                        >
                          <FiEye />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showModal && selectedPayment && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalBackdrop} onClick={closeModal}></div>
          <div className={styles.modalContainer}>
            <div className={styles.modalPanel}>
              <div className={styles.modalHeader}>
                <div className={styles.modalTitleSection}>
                  <h3>Payment Image Details</h3>
                  <p>
                    Uploaded on {formatDateTime(selectedPayment.uploaded_date)}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className={styles.modalCloseButton}
                  disabled={isApproving}
                >
                  <FaTimes />
                </button>
              </div>

              <div className={styles.modalGrid}>
                <div className={styles.modalInfoCard}>
                  <h4 className={styles.modalInfoTitle}>Payment Information</h4>
                  <div className={styles.modalInfoList}>
                    <div className={styles.modalInfoItem}>
                      <span className={styles.modalInfoLabel}>Payment ID:</span>
                      <span className={styles.modalInfoValue}>
                        {selectedPayment.payment_id}
                      </span>
                    </div>
                    <div className={styles.modalInfoItem}>
                      <span className={styles.modalInfoLabel}>Client ID:</span>
                      <span className={styles.modalInfoValue}>
                        {selectedPayment.client_id}
                      </span>
                    </div>
                    <div className={styles.modalInfoItem}>
                      <span className={styles.modalInfoLabel}>TSO Name:</span>
                      <span className={styles.modalInfoValue}>
                        {selectedPayment.tso_name || "-"}
                      </span>
                    </div>
                    <div className={styles.modalInfoItem}>
                      <span className={styles.modalInfoLabel}>Invoice No:</span>
                      <span className={styles.modalInfoValue}>
                        {selectedPayment.invoice_id || "-"}
                      </span>
                    </div>
                    <div className={styles.modalInfoItem}>
                      <span className={styles.modalInfoLabel}>
                        Invoice Amount:
                      </span>
                      <span className={styles.modalInfoValue}>
                        {formatCurrency(selectedPayment.payment_amount)}
                      </span>
                    </div>
                    {invoiceDetails && (
                      <>
                        <div className={styles.modalInfoItem}>
                          <span className={styles.modalInfoLabel}>
                            Invoice Amount:
                          </span>
                          <span className={styles.modalInfoValue}>
                            <FaMoneyBillWave className={styles.currencyIcon} />
                            {formatCurrency(invoiceDetails.amount)}
                          </span>
                        </div>
                        <div className={styles.modalInfoItem}>
                          <span className={styles.modalInfoLabel}>
                            Balance:
                          </span>
                          <span className={styles.modalInfoValue}>
                            <FaMoneyBillWave className={styles.currencyIcon} />
                            {formatCurrency(invoiceDetails.balance)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className={styles.modalInfoCard}>
                  <h4 className={styles.modalInfoTitle}>File Information</h4>
                  <div className={styles.modalInfoList}>
                    <div className={styles.modalInfoItem}>
                      <span className={styles.modalInfoLabel}>Filename:</span>
                      <span className={styles.modalInfoValue}>
                        {selectedPayment.stored_filename}
                      </span>
                    </div>
                    <div className={styles.modalInfoItem}>
                      <span className={styles.modalInfoLabel}>
                        Upload Date:
                      </span>
                      <span className={styles.modalInfoValue}>
                        {formatDateTime(selectedPayment.uploaded_date)}
                      </span>
                    </div>
                    <div className={styles.modalInfoItem}>
                      <span className={styles.modalInfoLabel}>
                        Last Updated:
                      </span>
                      <span className={styles.modalInfoValue}>
                        {formatDateTime(selectedPayment.updated_date)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={styles.modalInfoCard}>
                  <h4 className={styles.modalInfoTitle}>Payment Approval</h4>
                  <div className={styles.modalInfoList}>
                    <div className={styles.modalInfoItem}>
                      <span className={styles.modalInfoLabel}>Status:</span>
                      <span className={styles.modalInfoValue}>
                        <span
                          className={`${styles.statusBadge} ${
                            approvalStatus === "approved"
                              ? styles.statusApproved
                              : styles.statusPending
                          }`}
                        >
                          {approvalStatus}
                        </span>
                      </span>
                    </div>
                    <div className={styles.modalInfoItem}>
                      <span className={styles.modalInfoLabel}>Amount:</span>
                      <input
                        type="number"
                        value={amount}
                        onChange={handleAmountChange}
                        className={styles.modalInput}
                        placeholder="Enter amount"
                        min="0"
                        step="0.01"
                        disabled={approvalStatus === "approved" || isApproving}
                      />
                    </div>
                    <div className={styles.modalInfoItem}>
                      <span className={styles.modalInfoLabel}>
                        Payment Type:
                      </span>
                      <select
                        value={paymentType}
                        onChange={handlePaymentTypeChange}
                        className={styles.paymentTypeSelect}
                        disabled={approvalStatus === "approved" || isApproving}
                      >
                        <option value="">Select payment type</option>
                        {paymentTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.modalInfoItem}>
                      <span className={styles.modalInfoLabel}>
                        Description:
                      </span>
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className={styles.modalTextarea}
                        placeholder="Enter payment description"
                        rows="3"
                        disabled={approvalStatus === "approved" || isApproving}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.imageContainer}>
                <h4 className={styles.imageTitle}>Payment Image</h4>
                <div className={styles.imageWrapper}>
                  {imageLoading && (
                    <div className={styles.imageLoadingContainer}>
                      <FaSyncAlt className={styles.imageLoadingSpinner} />
                    </div>
                  )}
                  {imageUrl && (
                    <img
                      src={imageUrl}
                      alt="Payment verification"
                      className={styles.paymentImage}
                      style={{ display: imageLoading ? "none" : "block" }}
                    />
                  )}
                  {!imageLoading && !imageUrl && (
                    <p>
                      Image could not be loaded. It may be unavailable or an
                      error occurred.
                    </p>
                  )}
                </div>
              </div>

              <div className={styles.modalActions}>
                <button
                  onClick={closeModal}
                  className={`${styles.modalButton} ${styles.secondary}`}
                  disabled={isApproving}
                >
                  Close
                </button>
                <button
                  onClick={handleDownload}
                  className={`${styles.modalButton} ${styles.primary}`}
                  disabled={isApproving}
                >
                  Download Image
                </button>
                {approvalStatus !== "approved" && (
                  <button
                    onClick={handleApprovePayment}
                    className={`${styles.modalButton} ${styles.approveButton}`}
                    disabled={isApproving}
                  >
                    {isApproving ? (
                      <>
                        <FaSyncAlt className={styles.buttonSpinner} />
                        Approving...
                      </>
                    ) : (
                      "Approve Payment"
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnsitePayments;
